/**
 * MeetingNotesPanel — Notes / Decisions / Actions tabs (Feb 2026 · sprint 374).
 *
 * Consumes an existing meeting session id + review id. Fires backend on
 * every submit. `pendingContext` (from parent) attaches the last-clicked
 * cell/range as { sheet_index, target_ref, context_label, captured_value }.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { X, MessageSquare, Gavel, ListChecks, Plus, Calendar as CalendarIcon } from 'lucide-react';
import { ActionDetailModal } from './ActionDetailModal';

const TABS = [
  { k: 'notes',     label: 'Notes',     Icon: MessageSquare, testid: 'mn-tab-notes' },
  { k: 'decisions', label: 'Decisions', Icon: Gavel,         testid: 'mn-tab-decisions' },
  { k: 'actions',   label: 'Actions',   Icon: ListChecks,    testid: 'mn-tab-actions' },
];

/** sprint 376 · Elegant date field.
 * When empty, only the calendar icon is visible (no "dd/mm/yyyy" placeholder).
 * When a date is selected, it renders normally as an ISO YYYY-MM-DD chip that
 * opens the native picker on click. Reuses the browser's native picker so
 * accessibility + keyboard behaviour are preserved.
 */
function DateField({ value, onChange, testid }) {
  const ref = useMemo(() => ({ current: null }), []);
  const openPicker = () => {
    if (!ref.current) return;
    if (typeof ref.current.showPicker === 'function') {
      try { ref.current.showPicker(); return; } catch { /* fall-through */ }
    }
    ref.current.focus();
    ref.current.click();
  };
  const hasValue = !!value;
  return (
    <div className="relative mt-1" data-testid={`${testid}-wrap`}>
      {/* Hidden native input drives the picker + form value */}
      <input
        ref={(el) => { ref.current = el; }}
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label="Due date"
      />
      {/* Visible chrome */}
      <button type="button" onClick={openPicker}
              data-testid={`${testid}-open`}
              className={`h-8 w-full flex items-center gap-1.5 px-2 border border-[#E7EBE6] rounded bg-white text-xs text-left ${
                hasValue ? 'text-[#1A2421]' : 'text-[#B4C0BB] italic'}`}
              aria-label="Pick a date">
        <CalendarIcon className="w-3.5 h-3.5 text-[#7A8B82] shrink-0"/>
        <span className="truncate">{hasValue ? value : ''}</span>
        {hasValue && (
          <span onClick={(e) => { e.stopPropagation(); onChange(''); }}
                data-testid={`${testid}-clear`}
                className="ml-auto text-[#B4C0BB] hover:text-red-600 text-[10px]">×</span>
        )}
      </button>
    </div>
  );
}

export function MeetingNotesPanel({
  reviewId, sessionId, review, pendingContext, onClose, decisionsLocked,
}) {
  const { API, getHeaders } = useAuth();
  const [tab, setTab] = useState('notes');
  const [notes, setNotes] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [actions, setActions] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [allOutlets, setAllOutlets] = useState([]);   // sprint 376 · outlet selector pool

  const load = useCallback(async () => {
    try {
      const [n, d, a] = await Promise.all([
        fetch(`${API}/reviews/${reviewId}/notes`, { headers: getHeaders() }).then(r => r.json()),
        fetch(`${API}/reviews/${reviewId}/decisions`, { headers: getHeaders() }).then(r => r.json()),
        fetch(`${API}/reviews/${reviewId}/actions`, { headers: getHeaders() }).then(r => r.json()),
      ]);
      setNotes(n.items || []);
      setDecisions(d.items || []);
      setActions(a.items || []);
    } catch { toast.error('Failed to load meeting artefacts'); }
  }, [API, getHeaders, reviewId]);
  useEffect(() => { load(); }, [load]);

  // sprint 376 — Load the outlet pool this action can be assigned to.
  //  • outlet scope   → single fixed outlet (from review).
  //  • portfolio scope→ outlets in that portfolio (intersect with the
  //                     user's assigned outlets when non-admin, server
  //                     will re-check anyway).
  useEffect(() => {
    if (!review) return;
    const scope = review.scope_type || 'outlet';
    if (scope === 'outlet') {
      setAllOutlets([{ id: review.outlet_id, name: review.outlet_name || 'Outlet' }]);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/outlets`, { headers: getHeaders() });
        if (!r.ok) return;
        const list = await r.json();
        const pid = review.portfolio_id || review.scope_id;
        const filtered = (list || []).filter(o => !pid || o.portfolio_id === pid);
        setAllOutlets(filtered);
      } catch { /* silent */ }
    })();
  }, [review, API, getHeaders]);

  const scopeIsOutlet = (review?.scope_type || 'outlet') === 'outlet';
  const defaultOutletId = scopeIsOutlet
    ? (review?.outlet_id || '')
    : (allOutlets[0]?.id || '');

  const post = async (path, body) => {
    const r = await fetch(`${API}/reviews/${reviewId}/${path}`, {
      method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, ...body, ...(pendingContext || {}) }),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail.detail || 'Request failed');
    }
    return r.json();
  };

  // Note-specific form state (kept simple to avoid a big form component).
  const [noteDraft, setNoteDraft] = useState('');
  const [decisionDraft, setDecisionDraft] = useState('');
  const [actionDraft, setActionDraft] = useState({
    task: '', owner_text: '', outlet_id: '', due_date: '', priority: 'normal',
  });

  // Keep the composer's outlet_id in sync with the review scope by default.
  useEffect(() => {
    setActionDraft(d => ({ ...d, outlet_id: d.outlet_id || defaultOutletId }));
  }, [defaultOutletId]);

  const submitNote = async () => {
    if (!noteDraft.trim()) return;
    try { await post('notes', { text: noteDraft.trim() }); setNoteDraft(''); load(); toast.success('Note added'); }
    catch { toast.error('Failed'); }
  };
  const submitDecision = async () => {
    if (!decisionDraft.trim()) return;
    try { await post('decisions', { text: decisionDraft.trim() }); setDecisionDraft(''); load(); toast.success('Decision recorded'); }
    catch { toast.error('Failed'); }
  };
  const submitAction = async () => {
    if (!actionDraft.task.trim()) return;
    if (!actionDraft.outlet_id) return toast.error('Select an outlet');
    try {
      await post('actions', {
        task: actionDraft.task.trim(),
        owner_text: actionDraft.owner_text.trim() || null,
        outlet_id: actionDraft.outlet_id,
        due_date: actionDraft.due_date || null,
        priority: actionDraft.priority,
        status: 'open',
      });
      setActionDraft({
        task: '', owner_text: '', outlet_id: defaultOutletId,
        due_date: '', priority: 'normal',
      });
      load(); toast.success('Action created');
    } catch (e) { toast.error(e?.message || 'Failed'); }
  };

  const createActionFromNote = async (n) => {
    try {
      await post('actions', {
        task: n.text.slice(0, 500),
        outlet_id: defaultOutletId,
        from_note_id: n.id,
        status: 'open', priority: 'normal',
        sheet_index: n.sheet_index, target_ref: n.target_ref,
        context_label: n.context_label,
      });
      load(); toast.success('Action created from note');
    } catch (e) { toast.error(e?.message || 'Failed'); }
  };
  const createActionFromDecision = async (d) => {
    try {
      await post('actions', {
        task: d.text.slice(0, 500),
        outlet_id: defaultOutletId,
        from_decision_id: d.id,
        status: 'open', priority: 'normal',
        sheet_index: d.sheet_index, target_ref: d.target_ref,
        context_label: d.context_label,
      });
      load(); toast.success('Action created from decision');
    } catch (e) { toast.error(e?.message || 'Failed'); }
  };

  const addClarification = async (decision) => {
    const text = window.prompt('Clarification to add (original decision remains preserved):');
    if (!text?.trim()) return;
    try {
      const r = await fetch(`${API}/reviews/${reviewId}/decisions/${decision.id}/clarifications`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      if (!r.ok) throw new Error();
      load();
      toast.success('Clarification added');
    } catch { toast.error('Failed'); }
  };

  const patchAction = async (a, patch) => {
    try {
      const r = await fetch(`${API}/reviews/${reviewId}/actions/${a.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error();
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-[#E7EBE6]" data-testid="meeting-notes-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E7EBE6] bg-[#F9FBF7]">
        <h3 className="text-sm font-semibold text-[color:var(--color-text-primary)]">Meeting Notes</h3>
        <button onClick={onClose} data-testid="mn-close" className="p-1 text-[#7A8B82] hover:text-red-600">
          <X className="w-4 h-4"/>
        </button>
      </div>
      <div className="flex border-b border-[#E7EBE6]">
        {TABS.map(({ k, label, Icon, testid }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={testid}
                  className={`flex-1 py-2 text-xs font-medium border-b-2 inline-flex items-center justify-center gap-1 ${
                    tab === k ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary-hover)]'
                              : 'border-transparent text-[#5C6B64]'}`}>
            <Icon className="w-3.5 h-3.5"/> {label} <span className="opacity-60">
              ({k === 'notes' ? notes.length : k === 'decisions' ? decisions.length : actions.length})
            </span>
          </button>
        ))}
      </div>

      {pendingContext?.context_label && (
        <div className="px-3 py-1.5 bg-[color:var(--color-primary-tint)]/40 border-b border-[#E7EBE6]"
             data-testid="mn-pending-context">
          <span className="text-[11px] text-[color:var(--color-primary-hover)]">
            Context: <span className="font-semibold">{pendingContext.context_label}</span>
          </span>
        </div>
      )}

      <div className="flex-1 overflow-auto p-3 space-y-3">
        {tab === 'notes' && (
          <>
            {notes.map(n => (
              <div key={n.id} className="border border-[#EEF1EC] rounded-md p-2.5 text-xs bg-white"
                   data-testid={`mn-note-${n.id}`}>
                <p className="text-[#1A2421] whitespace-pre-wrap">{n.text}</p>
                {n.context_label && (
                  <p className="text-[10px] text-[color:var(--color-primary-hover)] mt-1">
                    on: {n.context_label} {n.captured_value != null && <>· <b>{String(n.captured_value)}</b></>}
                  </p>
                )}
                <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[#7A8B82]">
                  <span>{n.author_email}</span>
                  <span>·</span>
                  <span>{new Date(n.created_at).toLocaleString()}</span>
                  <button onClick={() => createActionFromNote(n)}
                          data-testid={`mn-note-to-action-${n.id}`}
                          className="ml-auto text-[color:var(--color-primary-hover)] hover:underline">
                    Add as Action
                  </button>
                </div>
              </div>
            ))}
            {!notes.length && <p className="text-xs text-[#B4C0BB] italic text-center py-4">No notes yet.</p>}
          </>
        )}

        {tab === 'decisions' && (
          <>
            {decisions.map(d => (
              <div key={d.id} className="border border-[#EEF1EC] rounded-md p-2.5 text-xs bg-white"
                   data-testid={`mn-decision-${d.id}`}>
                <div className="flex items-start justify-between">
                  <p className="text-[#1A2421] whitespace-pre-wrap font-medium">{d.text}</p>
                  {d.locked && (
                    <span className="text-[9px] uppercase tracking-wider bg-[#F3F5F2] text-[#5C6B64] px-1.5 py-0.5 rounded"
                          data-testid={`mn-decision-locked-${d.id}`}>Locked</span>
                  )}
                </div>
                {d.context_label && (
                  <p className="text-[10px] text-[color:var(--color-primary-hover)] mt-1">on: {d.context_label}</p>
                )}
                {d.clarifications?.map(c => (
                  <div key={c.id} className="mt-1.5 pl-2 border-l-2 border-[color:var(--color-primary)]/30"
                       data-testid={`mn-clarification-${c.id}`}>
                    <p className="text-[11px] text-[#5C6B64] italic">Clarification: {c.text}</p>
                    <p className="text-[9px] text-[#B4C0BB]">{c.author_email} · {new Date(c.created_at).toLocaleString()}</p>
                  </div>
                ))}
                <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[#7A8B82]">
                  <span>{d.author_email} · {new Date(d.created_at).toLocaleString()}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => createActionFromDecision(d)}
                            data-testid={`mn-decision-to-action-${d.id}`}
                            className="text-[color:var(--color-primary-hover)] hover:underline">
                      Create Action
                    </button>
                    {d.locked && (
                      <button onClick={() => addClarification(d)}
                              data-testid={`mn-decision-clarify-${d.id}`}
                              className="text-[color:var(--color-primary-hover)] hover:underline">
                        Add Clarification
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!decisions.length && <p className="text-xs text-[#B4C0BB] italic text-center py-4">No decisions yet.</p>}
          </>
        )}

        {tab === 'actions' && (
          <>
            {/* sprint 376 · flat inline composer — no oversized nested card */}
            <div className="pb-3 mb-2 border-b border-[#EEF1EC]" data-testid="mn-action-composer">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-widest text-[#7A8B82] font-semibold">New Action</span>
                {pendingContext?.context_label && (
                  <span className="text-[10px] text-[color:var(--color-primary-hover)]">
                    · on {pendingContext.context_label}
                  </span>
                )}
              </div>

              {/* Task — larger, most prominent */}
              <Label className="text-[10px] text-[#5C6B64]">Task</Label>
              <textarea value={actionDraft.task}
                        onChange={(e) => setActionDraft(d => ({ ...d, task: e.target.value }))}
                        rows={3}
                        placeholder="What needs to happen?"
                        data-testid="mn-action-task"
                        className="w-full mt-1 mb-2.5 text-sm border border-[#E7EBE6] rounded p-2 resize-y min-h-[64px] outline-none focus:border-[color:var(--color-primary)]"/>

              {/* Outlet + Owner side-by-side */}
              <div className="grid grid-cols-2 gap-2 mb-2.5">
                <div>
                  <Label className="text-[10px] text-[#5C6B64]">Outlet</Label>
                  {scopeIsOutlet ? (
                    <div data-testid="mn-action-outlet-fixed"
                          className="h-8 flex items-center px-2 mt-1 text-xs text-[#1A2421] bg-[#F3F5F2] border border-[#E7EBE6] rounded truncate">
                      {allOutlets[0]?.name || review?.outlet_name || 'Outlet'}
                    </div>
                  ) : (
                    <select value={actionDraft.outlet_id}
                            onChange={(e) => setActionDraft(d => ({ ...d, outlet_id: e.target.value }))}
                            data-testid="mn-action-outlet-select"
                            className="h-8 w-full text-xs border border-[#E7EBE6] rounded px-2 mt-1 bg-white">
                      <option value="">Select outlet…</option>
                      {allOutlets.map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] text-[#5C6B64]">Owner</Label>
                  <Input value={actionDraft.owner_text}
                         onChange={(e) => setActionDraft(d => ({ ...d, owner_text: e.target.value }))}
                         placeholder="e.g. Purchase Manager" data-testid="mn-action-owner"
                         className="h-8 text-xs mt-1"/>
                </div>
              </div>

              {/* Due + Priority side-by-side; date is icon-first, no dd/mm/yyyy placeholder */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <Label className="text-[10px] text-[#5C6B64]">Due</Label>
                  <DateField value={actionDraft.due_date}
                              onChange={(v) => setActionDraft(d => ({ ...d, due_date: v }))}
                              testid="mn-action-due"/>
                </div>
                <div>
                  <Label className="text-[10px] text-[#5C6B64]">Priority</Label>
                  <select value={actionDraft.priority}
                          onChange={(e) => setActionDraft(d => ({ ...d, priority: e.target.value }))}
                          data-testid="mn-action-priority"
                          className="h-8 w-full text-xs border border-[#E7EBE6] rounded px-2 mt-1 bg-white">
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <Button size="sm" onClick={submitAction}
                       disabled={!actionDraft.task.trim() || !actionDraft.outlet_id}
                       data-testid="mn-action-add"
                       className="w-full h-8 text-xs bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1">
                <Plus className="w-3 h-3"/> Add Action
              </Button>
            </div>

            {/* Current actions list */}
            <div className="text-[10px] uppercase tracking-widest text-[#7A8B82] font-semibold mb-1.5"
                  data-testid="mn-current-actions-heading">
              Current Actions
            </div>
            {actions.map(a => {
              const outletName = allOutlets.find(o => o.id === a.outlet_id)?.name
                || review?.outlet_name || '';
              return (
                <button key={a.id} onClick={() => setDetailId(a.id)}
                        data-testid={`mn-action-${a.id}`}
                        className="w-full text-left border border-[#EEF1EC] rounded-md p-2.5 text-xs bg-white hover:bg-[#F9FBF7] block">
                  <p className="text-[#1A2421] font-medium">{a.task}</p>
                  <p className="text-[10px] text-[#7A8B82] mt-0.5">
                    Outlet: <span className="text-[#1A2421]">{outletName || '—'}</span>
                    <span className="mx-1">·</span>
                    Owner: <span className="text-[#1A2421]">{a.owner_text || '—'}</span>
                    {a.due_date && <> · Due: <span className="text-[#1A2421]">{a.due_date}</span></>}
                    <span className="mx-1">·</span>
                    <span className={`uppercase tracking-wider text-[9px] px-1 py-0.5 rounded ${
                      a.status === 'done' ? 'bg-emerald-100 text-emerald-700' :
                      a.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      a.status === 'dropped' ? 'bg-gray-100 text-gray-600' :
                      'bg-amber-100 text-amber-700'}`}>{(a.status || '').replace('_', ' ')}</span>
                  </p>
                  <p className="text-[10px] text-[color:var(--color-primary-hover)] mt-0.5">
                    {(a.update_history || []).length} update{(a.update_history || []).length === 1 ? '' : 's'} · click to open
                  </p>
                </button>
              );
            })}
            {!actions.length && <p className="text-xs text-[#B4C0BB] italic text-center py-4">No actions yet.</p>}
          </>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#E7EBE6] p-2 bg-[#FDFDFC]">
        {tab === 'notes' && (
          <div className="flex flex-col gap-1.5">
            <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                      data-testid="mn-note-input" rows={2}
                      placeholder="Add a discussion note…"
                      className="w-full text-xs border border-[#E7EBE6] rounded p-1.5 resize-none outline-none focus:border-[color:var(--color-primary)]"/>
            <Button size="sm" onClick={submitNote} disabled={!noteDraft.trim()}
                    data-testid="mn-note-add"
                    className="h-7 text-[11px] bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1">
              <Plus className="w-3 h-3"/> Add Note
            </Button>
          </div>
        )}
        {tab === 'decisions' && (
          <div className="flex flex-col gap-1.5">
            <textarea value={decisionDraft} onChange={(e) => setDecisionDraft(e.target.value)}
                      data-testid="mn-decision-input" rows={2}
                      placeholder="Record a decision…"
                      className="w-full text-xs border border-[#E7EBE6] rounded p-1.5 resize-none outline-none focus:border-[color:var(--color-primary)]"/>
            <Button size="sm" onClick={submitDecision} disabled={!decisionDraft.trim()}
                    data-testid="mn-decision-add"
                    className="h-7 text-[11px] bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1">
              <Plus className="w-3 h-3"/> Add Decision
            </Button>
          </div>
        )}
        {tab === 'actions' && null}
      </div>

      {detailId && (
        <ActionDetailModal actionId={detailId}
                             onClose={() => setDetailId(null)}
                             onUpdated={load}/>
      )}
    </div>
  );
}
